import type { FrameSkipRounding } from "./types";

export function extractTimestepFromXml(xml: string): number | null {
  try {
    const m = xml.match(/<\s*option\b[^>]*\btimestep\s*=\s*"([^"]+)"/i);
    if (!m?.[1]) return null;
    const v = Number(m[1]);
    return Number.isFinite(v) && v > 0 ? v : null;
  } catch {
    return null;
  }
}

export function extractModelNameFromXml(xml: string): string | null {
  try {
    const parser = new DOMParser();
    const doc = parser.parseFromString(xml, "application/xml");
    const parseErr = doc.querySelector("parsererror");
    if (parseErr) return null;
    const root = doc.querySelector("mujoco");
    const name = root?.getAttribute?.("model");
    const cleaned = name ? String(name).trim() : "";
    return cleaned.length ? cleaned : null;
  } catch {
    return null;
  }
}

export function extractActuatorInfoFromXml(xml: string): {
  actuatorNames: string[];
  ctrlrange: Array<[number | null, number | null]>;
} {
  const parseRangeAttr = (raw: string | null): [number | null, number | null] | null => {
    if (!raw) return null;
    const parts = raw
      .trim()
      .split(/\s+/g)
      .map((s) => Number(s));
    if (parts.length < 2) return null;
    const lo = parts[0];
    const hi = parts[1];
    if (!Number.isFinite(lo) || !Number.isFinite(hi) || hi <= lo) return null;
    return [lo, hi];
  };

  try {
    const parser = new DOMParser();
    const doc = parser.parseFromString(xml, "application/xml");
    const parseErr = doc.querySelector("parsererror");
    if (parseErr) return { actuatorNames: [], ctrlrange: [] };
    const actuatorRoot = doc.querySelector("actuator");
    if (!actuatorRoot) return { actuatorNames: [], ctrlrange: [] };

    const names: string[] = [];
    const ranges: Array<[number | null, number | null]> = [];
    const jointRangeByName = new Map<string, [number, number]>();
    const joints = Array.from(doc.getElementsByTagName("joint"));
    for (const j of joints) {
      if (!j || typeof j.getAttribute !== "function") continue;
      const jointName = String(j.getAttribute("name") || "").trim();
      if (!jointName) continue;
      const jr = parseRangeAttr(j.getAttribute("range"));
      if (jr && jr[0] != null && jr[1] != null) {
        jointRangeByName.set(jointName, [jr[0], jr[1]]);
      }
    }
    const children = Array.from(actuatorRoot.children || []);
    for (let i = 0; i < children.length; i += 1) {
      const el = children[i];
      if (!el || typeof el.getAttribute !== "function") continue;
      const rawName = el.getAttribute("name");
      const name =
        rawName && String(rawName).trim().length
          ? String(rawName).trim()
          : `actuator_${names.length}`;
      names.push(name);

      const explicitRange = parseRangeAttr(el.getAttribute("ctrlrange"));
      if (explicitRange) {
        ranges.push(explicitRange);
        continue;
      }

      // Many MuJoCo models rely on inherited actuator ranges (e.g. position actuator with
      // inheritrange=1 in defaults). For export semantics, derive bounds from the referenced
      // joint range when ctrlrange is not explicitly present on the actuator element.
      const jointName = String(el.getAttribute("joint") || "").trim();
      if (jointName && jointRangeByName.has(jointName)) {
        ranges.push(jointRangeByName.get(jointName)!);
        continue;
      }

      ranges.push([null, null]);
    }
    return { actuatorNames: names, ctrlrange: ranges };
  } catch {
    return { actuatorNames: [], ctrlrange: [] };
  }
}

export function inferGripperActuatorIndex(actuatorNames: string[] | null) {
  if (!Array.isArray(actuatorNames) || actuatorNames.length === 0) return null;
  const idx = actuatorNames.findIndex((n) =>
    String(n || "")
      .toLowerCase()
      .includes("fingers_actuator")
  );
  return idx >= 0 ? idx : null;
}

export function toSafeZipPath(input: string) {
  let p = String(input || "");
  p = p.replace(/\\/g, "/");
  p = p.replace(/^\/*/, "");
  p = p.replace(/:/g, "_");
  while (p.includes("../")) p = p.replaceAll("../", "");
  while (p.includes("..\\")) p = p.replaceAll("..\\", "");
  return p || "asset";
}

export function preflightMissingMeshes(xml: string, fileMap: Record<string, File>) {
  const keys = Object.keys(fileMap || {});
  const normalizedKeys = keys.map((k) => String(k).replace(/\\/g, "/"));
  const normalizedKeysLower = normalizedKeys.map((k) => k.toLowerCase());
  const basenameSetLower = new Set(
    normalizedKeysLower.map((k) => {
      const parts = k.split("/");
      return parts[parts.length - 1] || "";
    })
  );
  const meshFileMatches = Array.from(xml.matchAll(/<mesh\b[^>]*\bfile="([^"]+)"/gi));
  const missingMeshes: string[] = [];
  for (const m of meshFileMatches) {
    const path = m?.[1];
    if (!path) continue;
    const normalizedPath = String(path).replace(/\\/g, "/");
    const normalizedPathLower = normalizedPath.toLowerCase();
    const pathBaseLower = normalizedPathLower.split("/").pop() || "";
    const has =
      normalizedKeysLower.some(
        (k) =>
          k === normalizedPathLower ||
          k.endsWith(`/${normalizedPathLower}`) ||
          k.endsWith(`\\${normalizedPathLower}`)
      ) || (pathBaseLower ? basenameSetLower.has(pathBaseLower) : false);
    if (!has) missingMeshes.push(path);
  }
  if (missingMeshes.length) {
    const uniq = Array.from(new Set(missingMeshes));
    throw new Error(
      `Env bundle export aborted: missing mesh assets referenced by XML:\n${uniq.map((p) => `- ${p}`).join("\n")}`
    );
  }
}

export function computeFrameSkip(args: {
  simDt: number | null;
  controlDt: number;
  rounding: FrameSkipRounding;
}) {
  if (!args.simDt || !Number.isFinite(args.simDt) || args.simDt <= 0) return 1;
  const raw = args.controlDt / args.simDt;
  if (!Number.isFinite(raw) || raw <= 1) return 1;
  switch (args.rounding) {
    case "round":
    default:
      return Math.max(1, Math.round(raw));
  }
}

export function parseXml(xml: string) {
  const parser = new DOMParser();
  const doc = parser.parseFromString(xml, "application/xml");
  const parseErr = doc.querySelector("parsererror");
  if (parseErr) throw new Error("Failed to parse generated model.xml (parsererror)");
  return doc;
}

function bodyHasFreeJoint(bodyEl: Element) {
  const children = Array.from(bodyEl.children || []);
  for (const el of children) {
    const tag = String(el.tagName || "").toLowerCase();
    if (tag === "freejoint") return true;
    if (tag === "joint") {
      const t = el.getAttribute("type");
      if (t && String(t).toLowerCase() === "free") return true;
    }
  }
  return false;
}

export function canonicalizeXmlForEnvBundle(xml: string) {
  // We want the env bundle fingerprint to represent *structure + assets*, not per-episode reset state.
  // Warm-start collection respawns free bodies (like the cube) by changing body pos/quat in the scene snapshot;
  // stripping pose attributes for free-joint bodies makes exported model.xml + fingerprints stable.
  const doc = parseXml(xml);
  const bodies = Array.from(doc.getElementsByTagName("body"));
  for (const b of bodies) {
    if (!bodyHasFreeJoint(b)) continue;
    b.removeAttribute("pos");
    b.removeAttribute("quat");
    b.removeAttribute("axisangle");
    b.removeAttribute("euler");
    b.removeAttribute("xyaxes");
  }
  return new XMLSerializer().serializeToString(doc);
}

export function rewriteMjcfAssetPathsForEnvBundle(xml: string) {
  const doc = parseXml(xml);
  const compiler = doc.querySelector("compiler") || doc.createElement("compiler");
  if (!compiler.parentNode) {
    const mujoco = doc.querySelector("mujoco");
    if (mujoco) mujoco.insertBefore(compiler, mujoco.firstChild);
  }

  const meshdir = getCompilerDirForRewrite(doc, "meshdir", "assets");
  const texturedir = getCompilerDirForRewrite(doc, "texturedir", "assets");

  doc.querySelectorAll("asset > mesh").forEach((meshEl) => {
    const attrName = meshEl.hasAttribute("file") ? "file" : meshEl.hasAttribute("obj") ? "obj" : "";
    if (!attrName) return;
    const zipPath = resolveMjcfAssetZipPathForRewrite(meshEl.getAttribute(attrName) || "", meshdir);
    if (zipPath) meshEl.setAttribute(attrName, zipPath);
  });

  doc.querySelectorAll("asset > texture").forEach((textureEl) => {
    const zipPath = resolveMjcfAssetZipPathForRewrite(
      textureEl.getAttribute("file") || "",
      texturedir
    );
    if (zipPath) textureEl.setAttribute("file", zipPath);
  });

  compiler.setAttribute("meshdir", "");
  compiler.setAttribute("texturedir", "");
  return new XMLSerializer().serializeToString(doc);
}

function getCompilerDirForRewrite(
  xmlDoc: Document,
  attr: "meshdir" | "texturedir",
  fallback: string
): string {
  const raw = xmlDoc.querySelector("compiler")?.getAttribute(attr);
  return String(raw || fallback)
    .replace(/\\/g, "/")
    .replace(/^\/+|\/+$/g, "");
}

function resolveMjcfAssetZipPathForRewrite(fileAttr: string, compilerDir: string): string {
  const filePath = String(fileAttr || "")
    .trim()
    .replace(/\\/g, "/")
    .replace(/^\/+/, "");
  if (!filePath) return "";
  if (!filePath.includes("/") && compilerDir) return toSafeZipPath(`${compilerDir}/${filePath}`);
  return toSafeZipPath(filePath);
}

export function xmlHasNamedElement(doc: Document, tagName: string, name: string) {
  const els = Array.from(doc.getElementsByTagName(tagName));
  for (const el of els) {
    if (!el || typeof el.getAttribute !== "function") continue;
    if (el.getAttribute("name") === name) return true;
  }
  return false;
}
